import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Plus, ArrowRight } from "@phosphor-icons/react";
import MarketingLayout from "@/components/MarketingLayout";
import SEO from "@/components/SEO";

const FAQS = [
  { q: "Is Nestled really free?", a: "Yes. The free plan holds up to 25 items in 5 locations forever. Upgrade only if you need more, want photos, voice input, QR labels, or family sharing." },
  { q: "How is Nestled different from a notes app or spreadsheet?", a: "Notes apps don't know about hierarchy or locations. Spreadsheets aren't searchable on your phone in 2 seconds. Nestled gives you photos, voice, QR labels, and an interface designed for *finding things*, not formatting them." },
  { q: "Who can see my stuff?", a: "Only you. Photos use owner-only signed access. Even if someone has the file path, they can't view your images without your session. If you share via QR, you choose public-or-private per location." },
  { q: "Can I share with my family?", a: "Yes — Family plan invites up to 5 household members who get the same inventory view. Invites go via secure email links." },
  { q: "What does the QR label actually do?", a: "It encodes a unique link for that location. Scan the sticker with any phone camera and you'll see what's inside. Public links show contents without login; private links require a household member." },
  { q: "Does voice input cost anything?", a: "It's included with Pro and Family. We use OpenAI Whisper under the hood — fast, accurate, no extra fees on top of your subscription." },
  { q: "What happens if I cancel?", a: "Your data stays. You drop back to the free tier with 25 items / 5 locations. We never delete your content." },
  { q: "Where is my data stored?", a: "On managed cloud infrastructure (MongoDB Atlas + object storage). We don't sell it, don't train models on it, and don't share it with third parties." },
  { q: "Can I export my inventory?", a: "Export is on our short-term roadmap. If it's a blocker for you, drop a note via Contact — we move features up based on real requests." },
  { q: "Do you have a mobile app?", a: "Yes — we're currently in closed beta testing on Android, gearing up for the Google Play Store launch. iOS is coming next. Want to try the beta and help us polish it? Head over to nestledapp.io/beta to grab the app and start testing today." },
];

export default function FAQ() {
  return (
    <MarketingLayout>
      <section className="max-w-3xl mx-auto px-6 md:px-10 pt-16 md:pt-20 pb-12 text-center">
        <span className="text-xs font-bold uppercase tracking-[0.18em] text-terracotta-600">Questions, gently answered</span>
        <h1 className="mt-3 font-heading text-4xl sm:text-5xl lg:text-6xl text-stoneink-900 font-semibold tracking-tight">
          You probably want to know…
        </h1>
        <p className="mt-5 text-stoneink-700 text-lg">If we missed yours, <Link to="/contact" className="text-terracotta-600 hover:text-terracotta-700 underline-offset-4 hover:underline">just ask</Link>.</p>
      </section>

      <section className="max-w-3xl mx-auto px-6 md:px-10 pb-20 space-y-3">
        {FAQS.map((item, i) => <FAQItem key={item.q} item={item} index={i} />)}
      </section>

      <section className="max-w-3xl mx-auto px-6 md:px-10 pb-24">
        <div className="rounded-3xl border border-sand-300 bg-white p-8 text-center shadow-ambient">
          <h2 className="font-heading text-2xl text-stoneink-900 font-medium">Ready to organize?</h2>
          <Link to="/register" className="mt-5 inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-terracotta-500 hover:bg-terracotta-600 text-white font-medium">
            Start free <ArrowRight weight="bold" size={16} />
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
}

function FAQItem({ item, index }) {
  const [open, setOpen] = useState(false);
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: Math.min(index, 8) * 0.03 }}
      className="rounded-2xl border border-sand-300 bg-white overflow-hidden">
      <button onClick={() => setOpen(!open)} data-testid={`faq-toggle-${index}`}
        className="w-full px-5 py-4 flex items-center justify-between gap-4 hover:bg-sand-100 text-left">
        <span className="font-medium text-stoneink-900">{item.q}</span>
        <Plus weight="bold" size={18} className={`text-stoneink-500 transition-transform ${open ? "rotate-45 text-terracotta-500" : ""}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18 }} className="overflow-hidden">
            <div className="px-5 pb-5 text-stoneink-700 leading-relaxed">{item.a}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
